import { useNavigate } from 'react-router-dom';
import { IoChevronBack } from 'react-icons/io5';
import { SEO } from '@/components/features/SEO';
import { ROUTES } from '@/constants/routes';
import styles from './About.module.css';

export function About() {
    const navigate = useNavigate();

    return (
        <div className={styles.page}>
            <SEO
                title="Sobre Nosotros - Peru IA | Quienes Somos"
                description="Peru IA es un proyecto independiente que utiliza inteligencia artificial para hacer accesible la informacion electoral oficial del JNE sobre las elecciones de Peru 2026."
                url="https://peruhonesto.com/about"
                keywords={[
                    "peru ia",
                    "quienes somos",
                    "proyecto electoral peru",
                    "informacion electoral peru 2026",
                    "voto informado"
                ]}
                schema={{
                    "@context": "https://schema.org",
                    "@type": "Organization",
                    "name": "Peru IA",
                    "url": "https://peruhonesto.com",
                    "logo": "https://peruhonesto.com/logo.png",
                    "description": "Proyecto independiente que utiliza inteligencia artificial para hacer accesible la informacion electoral oficial del JNE sobre las elecciones de Peru 2026.",
                    "foundingDate": "2025",
                    "sameAs": [],
                    "contactPoint": {
                        "@type": "ContactPoint",
                        "email": "info@qolca.org",
                        "contactType": "customer service"
                    }
                }}
            />

            <nav className={styles.nav}>
                <div className={styles.navInner}>
                    <button
                        className={styles.backButton}
                        onClick={() => navigate(ROUTES.home)}
                    >
                        <IoChevronBack /> Volver al inicio
                    </button>
                </div>
            </nav>

            <main className={styles.content}>
                <h1>Sobre Peru IA</h1>
                <p className={styles.subtitle}>
                    Un proyecto independiente para promover el voto informado en el Peru.
                </p>

                <section>
                    <h2>Nuestra Mision</h2>
                    <p>
                        Peru IA nacio con un objetivo claro: hacer que la informacion electoral sea accesible
                        para todos los peruanos. En un pais con decenas de partidos politicos y cientos de
                        candidatos, es practicamente imposible que un ciudadano lea todos los planes de gobierno
                        antes de votar.
                    </p>
                    <p>
                        Utilizamos inteligencia artificial para analizar los documentos oficiales registrados
                        ante el Jurado Nacional de Elecciones (JNE) y presentar la informacion de manera
                        clara, neutral y verificable. No hacemos recomendaciones de voto — solo facilitamos
                        el acceso a la informacion.
                    </p>
                </section>

                <section>
                    <h2>Como Funciona</h2>
                    <p>
                        Nuestra plataforma utiliza tecnologia de Retrieval-Augmented Generation (RAG)
                        para responder preguntas sobre candidatos y partidos politicos. Esto significa que
                        cada respuesta esta respaldada por documentos oficiales:
                    </p>
                    <ul>
                        <li>Planes de gobierno registrados ante el JNE</li>
                        <li>Hojas de vida de los candidatos</li>
                        <li>Informacion oficial del registro de organizaciones politicas</li>
                        <li>Datos del cronograma electoral oficial</li>
                    </ul>
                    <p>
                        Cada respuesta incluye referencias a sus fuentes, para que puedas verificar
                        la informacion por ti mismo.
                    </p>
                </section>

                <section>
                    <h2>Nuestros Valores</h2>
                    <div className={styles.values}>
                        <div className={styles.valueCard}>
                            <h3>Neutralidad</h3>
                            <p>
                                No favorecemos a ningun candidato ni partido politico. Presentamos
                                la informacion tal como aparece en los documentos oficiales.
                            </p>
                        </div>
                        <div className={styles.valueCard}>
                            <h3>Transparencia</h3>
                            <p>
                                Todas las respuestas incluyen sus fuentes. Indicamos claramente
                                cuando la IA no tiene informacion suficiente para responder.
                            </p>
                        </div>
                        <div className={styles.valueCard}>
                            <h3>Accesibilidad</h3>
                            <p>
                                La plataforma es gratuita y no requiere registro para comenzar a
                                hacer preguntas. La informacion electoral debe ser para todos.
                            </p>
                        </div>
                    </div>
                </section>

                <section>
                    <h2>Fuentes de Datos</h2>
                    <p>
                        Toda la informacion en nuestra plataforma proviene de fuentes oficiales y publicas:
                    </p>
                    <ul>
                        <li>
                            <strong>Jurado Nacional de Elecciones (JNE)</strong> — Planes de gobierno,
                            hojas de vida de candidatos, y registro de organizaciones politicas.
                        </li>
                        <li>
                            <strong>Oficina Nacional de Procesos Electorales (ONPE)</strong> — Cronograma
                            electoral y datos del proceso.
                        </li>
                        <li>
                            <strong>Registro Nacional de Identificacion y Estado Civil (RENIEC)</strong> —
                            Informacion sobre el padron electoral.
                        </li>
                    </ul>
                </section>

                <section>
                    <h2>Limitaciones</h2>
                    <p>
                        Es importante entender las limitaciones de nuestra herramienta:
                    </p>
                    <ul>
                        <li>La IA puede cometer errores de interpretacion. Siempre verifica la informacion con las fuentes originales.</li>
                        <li>No cubrimos noticias en tiempo real ni declaraciones de campana — solo documentos oficiales registrados.</li>
                        <li>No hacemos predicciones electorales ni recomendaciones de voto.</li>
                        <li>La informacion se actualiza periodicamente, pero puede no reflejar los cambios mas recientes.</li>
                    </ul>
                </section>

                <section>
                    <h2>Quienes Somos</h2>
                    <p>
                        Peru IA es un proyecto desarrollado por Qolca, una empresa de software con sede en Peru,
                        como un aporte a la comunidad peruana. Este proyecto no tiene afiliacion con ningun partido
                        politico, organizacion gubernamental ni candidato electoral.
                    </p>
                </section>

                <section>
                    <h2>Contacto</h2>
                    <p>
                        Si tienes preguntas, sugerencias o deseas reportar un error en la informacion,
                        puedes escribirnos a{' '}
                        <a href="mailto:info@qolca.org" className={styles.contactLink}>
                            info@qolca.org
                        </a>.
                    </p>
                </section>
            </main>
        </div>
    );
}
